import React, { useState, useRef, useCallback, useMemo } from 'react';
import { updateLearningProgramSlidePreviewService } from '../../../../../services/learning_programs.service';
import { toastWarning } from '../../../../../utils/utils';
import { tUi } from '../../../../../utils/dq_lc';
import { getSlideId } from '../../utils/LearnTab/preview.utils';

export const useSlideInlineEditor = ({
    data,
    nav,
    code: explicitCode,
    selectedSession = data?.selectedSession,
    currentSessionSlide = nav?.currentSessionSlide,
    currentSlide = nav?.currentSlide,
    sessionSlides = data?.sessionSlides,
    setSessionSlides = data?.setSessionSlides,
    cachedSlideData = data?.cachedSlideData,
    setCachedSlideData = data?.setCachedSlideData,
    availableSlideLanguages = data?.availableSlideLanguages ?? [],
    currentDisplayLanguage = data?.currentDisplayLanguage,
    setCurrentDisplayLanguage = data?.setCurrentDisplayLanguage,
    setSelectedPreviewLanguage = data?.setSelectedPreviewLanguage,
    setIntroVideoUrl = data?.setIntroVideoUrl,
    setIntroVideoVtt = data?.setIntroVideoVtt,
    setSegments = nav?.setSegments,
    buildSegmentsList = data?.buildSegmentsList,
    setAssessmentVisibility = data?.setAssessmentVisibility,
    readOnly = false,
    uiLocale = data?.uiLocale ?? 'en-US',
    preserveSlideIdRef = nav?.preserveSlideIdRef,
    preserveSlideIndexRef = nav?.preserveSlideIndexRef,
    isLanguageTogglingRef = nav?.isLanguageTogglingRef,
    isSavingRef = nav?.isSavingRef,
}) => {
    const code = explicitCode || data?.programData?.code;
    const [isEditMode, setIsEditMode] = useState(false);
    const [slideEdits, setSlideEdits] = useState({});
    const [currentEditLanguage, setCurrentEditLanguage] = useState('');
    const [showEnglishContent, setShowEnglishContent] = useState(false);

    const slideEditsRef = useRef({});
    const saveTimeoutRef = useRef(null);

    // Get slide-specific edits for the current slide
    const currentSlideEdits = useMemo(() => {
        const currSlideId = getSlideId(currentSessionSlide);
        if (!currSlideId) return {};

        const prefix = `${currSlideId}_`;
        const result = {};
        Object.keys(slideEdits).forEach(key => {
            if (key.startsWith(prefix)) {
                const fieldId = key.substring(prefix.length);
                result[fieldId] = slideEdits[key];
            }
        });
        return result;
    }, [slideEdits, currentSessionSlide]);

    // Helper to deep clone and apply edits to session slides
    const applyEditsToContents = useCallback((originalSlides, edits) => {
        if (!originalSlides) return {};

        const cloned = JSON.parse(JSON.stringify(originalSlides));

        Object.keys(cloned).forEach(segmentKey => {
            const segmentContent = cloned[segmentKey];
            const slidesToMap = Array.isArray(segmentContent) ? segmentContent : [segmentContent];

            slidesToMap.forEach(slide => {
                const slideId = getSlideId(slide);
                if (!slide || !slideId) return;

                const prefix = `${slideId}_`;

                Object.keys(edits).forEach(editKey => {
                    if (!editKey.startsWith(prefix)) return;

                    const fieldId = editKey.substring(prefix.length);
                    const editObj = edits[editKey];

                    let editedContent;
                    if (typeof editObj === 'string') {
                        editedContent = editObj;
                    } else if (editObj && typeof editObj === 'object') {
                        editedContent =
                            editObj.edited !== undefined
                                ? editObj.edited
                                : editObj.original;
                    }

                    if (editedContent === undefined) return;

                    // TITLE
                    if (fieldId === 'title') {
                        slide.SlideTitle = editedContent;
                    }

                    // CONTENT_0 (string or single-item array)
                    else if (fieldId === 'content_0') {
                        if (typeof slide.SlideContent === 'string') {
                            slide.SlideContent = editedContent;
                        } else if (Array.isArray(slide.SlideContent)) {
                            if (slide.SlideContent.length === 0) {
                                slide.SlideContent.push(editedContent);
                            } else if (typeof slide.SlideContent[0] === 'string') {
                                slide.SlideContent[0] = editedContent;
                            }
                        }
                    }

                    // content_X or content_X_title/text
                    else if (fieldId.startsWith('content_')) {
                        const parts = fieldId.split('_');
                        const idx = parseInt(parts[1], 10);

                        if (isNaN(idx)) return;

                        // Ensure SlideContent is array
                        if (!Array.isArray(slide.SlideContent)) {
                            slide.SlideContent = [];
                        }

                        if (!slide.SlideContent[idx]) {
                            slide.SlideContent[idx] = {};
                        }

                        // content_1
                        if (parts.length === 2) {
                            slide.SlideContent[idx] = editedContent;
                        }

                        // content_1_title / content_1_text
                        if (parts.length === 3) {
                            const subField = parts[2].toLowerCase();
                            if (subField === 'title') slide.SlideContent[idx].Title = editedContent;
                            if (subField === 'text') slide.SlideContent[idx].Text = editedContent;
                        }
                    }
                });
            });
        });

        return cloned;
    }, []);

    // Slide editor handlers
    const handleSaveEdit = useCallback((fieldId, edit) => {
        const currSlideId = getSlideId(currentSessionSlide);
        if (!currSlideId) return;

        // edit can be either a string (old format) or object (new format)
        const editObj = typeof edit === 'string'
            ? { original: '', edited: edit, language: currentEditLanguage }
            : edit;
        const key = `${currSlideId}_${fieldId}`;

        // Update Ref first to accumulate edits (fixes race condition between title/content saves)
        const nextSlideEdits = {
            ...slideEditsRef.current,
            [key]: editObj
        };
        slideEditsRef.current = nextSlideEdits;
        setSlideEdits(nextSlideEdits);

        // Debounce the API call to ensure we send the final accumulated state
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                if (!code || !selectedSession?.session_code) return;

                // Use the accumulated edits from Ref
                const fullSlideContent = applyEditsToContents(sessionSlides, slideEditsRef.current);

                const result = await updateLearningProgramSlidePreviewService(
                    code,
                    selectedSession.session_code,
                    currentDisplayLanguage,
                    fullSlideContent
                );

                // Update local state with the edited content so UI reflects changes immediately
                if (result?.data) {
                    console.log('Slide edits saved successfully, updating local state');
                    // Preserve current slide so UI stays on same slide after update
                    if (preserveSlideIdRef) preserveSlideIdRef.current = currSlideId ?? null;
                    if (preserveSlideIndexRef) preserveSlideIndexRef.current = currentSlide;
                    if (isSavingRef) isSavingRef.current = true;

                    setSessionSlides(fullSlideContent);

                    // Update cached slide data for the current language
                    if (cachedSlideData[currentDisplayLanguage]) {
                        setCachedSlideData(prev => ({
                            ...prev,
                            [currentDisplayLanguage]: {
                                ...prev[currentDisplayLanguage],
                                slide_preview: fullSlideContent
                            }
                        }));
                    }
                }
            } catch (error) {
                console.error('Failed to save slide edits:', error);
                toastWarning(tUi('preview.toast.failedSave', uiLocale));
            }
        }, 500); // 500ms debounce
    }, [currentSessionSlide, currentEditLanguage, code, selectedSession?.session_code, sessionSlides, currentDisplayLanguage, applyEditsToContents, cachedSlideData, uiLocale, setSessionSlides, setCachedSlideData, currentSlide, preserveSlideIdRef, preserveSlideIndexRef, isSavingRef]);

    const handleResetEdit = useCallback((fieldId) => {
        const currSlideId = getSlideId(currentSessionSlide);
        if (!currSlideId) return;
        const key = `${currSlideId}_${fieldId}`;

        const newEdits = { ...slideEditsRef.current };
        delete newEdits[key];

        slideEditsRef.current = newEdits;
        setSlideEdits(newEdits);
    }, [currentSessionSlide]);

    // Edit mode button handler
    const handleEditButtonClick = useCallback(() => {
        if (readOnly) return; // template preview — never enter edit mode
        if (isEditMode) {
            // Exiting edit mode
            setIsEditMode(false);

            // If not switching language, clear preserve refs so next segment change starts at slide 0
            if (!showEnglishContent || availableSlideLanguages.length <= 1) {
                if (preserveSlideIdRef) preserveSlideIdRef.current = null;
                if (preserveSlideIndexRef) preserveSlideIndexRef.current = null;
            }

            // If currently showing English, switch back to other language
            if (showEnglishContent && availableSlideLanguages.length > 1) {
                setShowEnglishContent(false);

                // Switch to non-English language
                const targetLanguage = availableSlideLanguages.find(lang => lang !== 'en-US') || availableSlideLanguages[0];
                const languageData = cachedSlideData[targetLanguage];

                if (languageData) {
                    if (preserveSlideIdRef) preserveSlideIdRef.current = getSlideId(currentSessionSlide) ?? null;
                    if (preserveSlideIndexRef) preserveSlideIndexRef.current = currentSlide;
                    if (isLanguageTogglingRef) isLanguageTogglingRef.current = true;
                    setIntroVideoUrl(languageData.IntroVideo ?? null);
                    setIntroVideoVtt(languageData.IntroVideoVtt ?? null);
                    setSessionSlides(languageData.slide_preview);
                    setCurrentDisplayLanguage(targetLanguage);
                    setSegments(buildSegmentsList(languageData.slide_preview, selectedSession));

                    if (targetLanguage !== 'en-US') {
                        setCurrentEditLanguage(targetLanguage.split('-')[0]);
                    }

                    if (selectedSession) {
                        const { show_pre_assessment = true, show_post_assessment = true } = languageData;
                        setAssessmentVisibility(prev => ({
                            ...prev,
                            [selectedSession.session_code]: { show_pre_assessment, show_post_assessment }
                        }));
                    }

                    console.log(`Exited edit mode - switched back to ${targetLanguage} content`);
                }
            }
        } else {
            // Entering edit mode
            // Check if there are no other languages available (English-only)
            if (availableSlideLanguages.length <= 1 && availableSlideLanguages.includes('en-US')) {
                toastWarning(tUi('preview.toast.editNotAvailableEnglish', uiLocale));
                return;
            }
            // Check if currently showing English in a multi-language program
            if ((currentEditLanguage === 'en' || currentDisplayLanguage === 'en-US') && availableSlideLanguages.length > 1) {
                toastWarning(tUi('preview.toast.editToggleLanguage', uiLocale));
                return;
            }
            setIsEditMode(true);
        }
    }, [readOnly, isEditMode, showEnglishContent, availableSlideLanguages, cachedSlideData, buildSegmentsList, selectedSession, currentEditLanguage, currentDisplayLanguage, currentSessionSlide, currentSlide, setIntroVideoUrl, setIntroVideoVtt, setSessionSlides, setCurrentDisplayLanguage, setSegments, setAssessmentVisibility, uiLocale, preserveSlideIdRef, preserveSlideIndexRef, isLanguageTogglingRef]);

    // Direct language selector handler (supports switching to any target language, e.g. KO, ES, EN)
    const handleSelectLanguage = useCallback((targetLanguage) => {
        if (!targetLanguage || !cachedSlideData[targetLanguage]) return;

        const isEnglish = targetLanguage === 'en-US';
        setShowEnglishContent(isEnglish && availableSlideLanguages.length > 1);

        const languageData = cachedSlideData[targetLanguage];
        if (!languageData) return;

        // Preserve current slide so UI stays on same slide after language toggle
        if (preserveSlideIdRef) preserveSlideIdRef.current = getSlideId(currentSessionSlide) ?? null;
        if (preserveSlideIndexRef) preserveSlideIndexRef.current = currentSlide;
        if (isLanguageTogglingRef) isLanguageTogglingRef.current = true;

        setIntroVideoUrl(languageData.IntroVideo ?? null);
        setIntroVideoVtt(languageData.IntroVideoVtt ?? null);
        setSessionSlides(languageData.slide_preview);
        setCurrentDisplayLanguage(targetLanguage);
        if (setSelectedPreviewLanguage) {
            setSelectedPreviewLanguage(targetLanguage);
        }

        // Update segments without resetting position
        setSegments(buildSegmentsList(languageData.slide_preview, selectedSession));

        // Set edit language (non-English)
        if (targetLanguage !== 'en-US') {
            setCurrentEditLanguage(targetLanguage.split('-')[0]);
        }

        // Update assessment visibility
        if (selectedSession) {
            const { show_pre_assessment = true, show_post_assessment = true } = languageData;
            setAssessmentVisibility(prev => ({
                ...prev,
                [selectedSession.session_code]: { show_pre_assessment, show_post_assessment }
            }));
        }

        console.log(`Switched to ${targetLanguage} content`);
    }, [cachedSlideData, availableSlideLanguages, currentSessionSlide, currentSlide, preserveSlideIdRef, preserveSlideIndexRef, isLanguageTogglingRef, selectedSession, buildSegmentsList, setSegments, setIntroVideoUrl, setIntroVideoVtt, setSessionSlides, setCurrentDisplayLanguage, setSelectedPreviewLanguage, setCurrentEditLanguage, setShowEnglishContent, setAssessmentVisibility]);

    // Toggle between English and other language content (for binary switch backwards-compatibility)
    const handleLanguageToggle = useCallback((e) => {
        const showEnglish = e.target.checked;
        const targetLanguage = showEnglish
            ? 'en-US'
            : availableSlideLanguages.find(lang => lang !== 'en-US') || availableSlideLanguages[0];
        handleSelectLanguage(targetLanguage);
    }, [availableSlideLanguages, handleSelectLanguage]);

    return {
        isEditMode,
        setIsEditMode,
        slideEdits,
        setSlideEdits,
        currentEditLanguage,
        setCurrentEditLanguage,
        showEnglishContent,
        setShowEnglishContent,
        currentSlideEdits,
        applyEditsToContents,
        handleSaveEdit,
        handleResetEdit,
        handleEditButtonClick,
        handleLanguageToggle,
        handleSelectLanguage,
    };
};

export default useSlideInlineEditor;
